import React, { useEffect, useMemo, useState } from "react";
import { Loader, Message, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { EnvironmentalObjetivesform, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { renderTemplate, fillTemplate } from "../../../../utils/renderTemplate";
import { BasicModal, ConfirmModal } from "../../../Shared";
import { EnvironmentalObjetivesForm } from "../EnvironmentalObjetivesForm";
import { fetchBaselineYearsByAspect, getBaselineYearForKpi } from "../../../../utils/dataAndFormulas/kpiBaselineYear";
import {
  avanceDe,
  avancePonderado,
  esperadoDe,
  esperadoHasta,
  cumplimientoAnual,
  cumplimientoAnualMeta,
} from "../../../../utils/dataAndFormulas/objectivesCalendarCalc";
import "./StrategicPlan.scss";

const environmentalObjetivesFormController = new EnvironmentalObjetivesform();
const siteController = new Site();

// Piso mínimo del horizonte si el sitio todavía no configuró uno propio
// (ver ObjectivesMatrix.js, donde se edita) — nunca trunca un objetivo cuyo
// año meta ya esté más adelante.
const HORIZON = 5;

const ASPECT_COLOR = {
  energy: "#1A3562",
  water: "#5E89B4",
  waste: "#A8C4DC",
  effluent: "#7BA3C8",
  fuels: "#2D5F8A",
  health_safety: "#8A5A08",
  social: "#6B4FA0",
};

const STATUS_LABEL_KEY = {
  completed: "sp_status_cumplido",
  in_progress: "sp_status_en_progreso",
  planned: "sp_status_planificado",
};

// El avance de cada objetivo se deriva de sus metas (modelo calendario:
// cumplidas/programadas) — ver client/src/utils/dataAndFormulas/objectivesCalendarCalc.js.
// Ya no se carga a mano (contribution/progress, modelo viejo).
function statusOf(o) {
  const av = avanceDe(o);
  return av >= 1 ? "completed" : av > 0 ? "in_progress" : "planned";
}
// Vencido: el año meta ya pasó y todavía no llegó al 100%, pero nadie lo
// extendió (editó el año meta) todavía — necesita una decisión.
function isOverdueObjective(o) {
  return (o.year || 0) < new Date().getFullYear() && avanceDe(o) < 1;
}
// Año de línea base de ESTE objetivo. Prioridad: 1) o.baselineYear congelado
// al crearlo (no se corre si la config de Línea Base cambia después); 2)
// lectura en vivo, solo para objetivos viejos sin el campo guardado; 3) año
// meta − 1 como último recurso.
function getBaseYearForObjetivo(o, formByAspect) {
  return o.baselineYear ?? getBaselineYearForKpi(o.aspect, o.kpiCode, formByAspect) ?? (o.year || 0) - 1;
}
// El KPI todavía no tiene línea base configurada en ☰ → Datos → Línea Base
// (ni congelada en el objetivo ni disponible en vivo) — se está proyectando
// con año meta − 1 como suposición, no con un dato real.
function isBaseYearEstimated(o, formByAspect) {
  return o.baselineYear == null && getBaselineYearForKpi(o.aspect, o.kpiCode, formByAspect) == null;
}
function statusChip(av, t) {
  if (av >= 1) return { cls: "ok", txt: `✔ ${t("sp_status_cumplido")}` };
  if (av >= 0.5) return { cls: "warn", txt: `◐ ${t("sp_status_en_avance")}` };
  if (av > 0) return { cls: "bad", txt: `◔ ${t("sp_status_inicial")}` };
  return { cls: "none", txt: `○ ${t("sp_status_no_data")}` };
}
function progressColor(av) {
  if (av >= 1) return "#7CC48A";
  if (av >= 0.5) return "#F0D479";
  if (av > 0) return "#EFA1AB";
  return "#D4E5F0";
}

// Senda ("programado a hoy", v6.3): el esperado sale del calendario que
// programó el usuario, no de una rampa lineal teórica — ver esperadoDe() en
// objectivesCalendarCalc.js. Si la programación todavía no arrancó, esperado
// es 0 y por lo tanto siempre "al día" (nunca "atrasado").
function sendaTxt(av, esperado, t) {
  if (av >= esperado) return { ok: true, txt: t("sp_senda_al_dia") };
  const pp = Math.round((esperado - av) * 100);
  return { ok: false, txt: fillTemplate(t("sp_senda_atras_template"), { pp }) };
}

// "2026-03-15" -> "mar-26" (mes abreviado en español + año), columna "Inicio"
// del Nivel 2 — mismo formato que usa la vista previa del formulario SMART.
const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function formatMonthYearAbbrev(isoDate) {
  if (!isoDate) return null;
  const datePart = String(isoDate).slice(0, 10);
  const [y, m] = datePart.split("-");
  if (!y || !m) return null;
  return `${MONTHS_ES[Number(m) - 1]}-${y.slice(-2)}`;
}

export function StrategicPlan({ siteSelected }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [objectives, setObjectives] = useState([]);
  const [formByAspect, setFormByAspect] = useState({});
  const [loading, setLoading] = useState(false);
  // Horizonte configurado en la Matriz (Paso 2) — acá es de solo lectura,
  // solo para que la Trayectoria muestre el mismo rango de años.
  const [horizonYear, setHorizonYear] = useState(null);

  // ── editar / borrar objetivo (Nivel 2) ──
  const [editingObjective, setEditingObjective] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    if (!siteSelected || !accessToken) return;
    (async () => {
      try {
        const res = await siteController.getSite(accessToken, siteSelected);
        setHorizonYear(res?.horizonYear || null);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [siteSelected, accessToken]);

  const load = async () => {
    if (!siteSelected || !accessToken) return;
    setLoading(true);
    try {
      const response = await environmentalObjetivesFormController.getEnvironmentalObjetivesFormsBySite(
        accessToken,
        siteSelected
      );
      const objs = response.code === 200 ? response.objetives || [] : [];
      setObjectives(objs);
      const formByAspectResult = await fetchBaselineYearsByAspect(
        accessToken,
        siteSelected,
        objs.filter((o) => o.active !== false).map((o) => o.aspect)
      );
      setFormByAspect(formByAspectResult);
    } catch (error) {
      console.error(error);
      setObjectives([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [siteSelected, accessToken]);

  // ── editar / borrar objetivo (Nivel 2) ──
  const openEditObjective = (o) => setEditingObjective(o);
  const closeEditObjective = () => setEditingObjective(null);

  const openDeleteObjective = (id) => setDeleteTargetId(id);
  const closeDeleteObjective = () => setDeleteTargetId(null);
  const confirmDeleteObjective = async () => {
    try {
      await environmentalObjetivesFormController.deleteEnvironmentalObjetivesForm(accessToken, deleteTargetId);
      closeDeleteObjective();
      load();
    } catch (error) {
      console.error(error);
      toast.error(t("sp_toast_delete_error"));
    }
  };

  const activeObjectives = useMemo(() => objectives.filter((o) => o.active !== false), [objectives]);

  const global = useMemo(
    () => avancePonderado(activeObjectives, avanceDe, "reductionTons"),
    [activeObjectives]
  );

  const hoyIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const esperadoGlobal = useMemo(
    () => avancePonderado(activeObjectives, (o) => esperadoDe(o, hoyIso), "reductionTons"),
    [activeObjectives, hoyIso]
  );
  const sendaGlobal = sendaTxt(global, esperadoGlobal, t);

  const cumplidos = activeObjectives.filter((o) => avanceDe(o) >= 1).length;
  const totalMetas = activeObjectives.reduce((s, o) => s + (o.metas || []).length, 0);
  const inversionTotal = activeObjectives.reduce(
    (s, o) => s + (o.metas || []).reduce((s2, m) => s2 + (parseInt((m.investment || "").replace(/[^\d]/g, ""), 10) || 0), 0),
    0
  );

  // ── objetivos agrupados por aspecto (v6.1) — bandas del Nivel 2 ──
  const aspectGroups = useMemo(() => {
    const order = [];
    const byAspect = {};
    activeObjectives.forEach((o) => {
      const asp = o.aspect || "—";
      if (!byAspect[asp]) { byAspect[asp] = []; order.push(asp); }
      byAspect[asp].push(o);
    });
    return order.map((asp) => ({
      aspect: asp,
      objetivos: byAspect[asp],
      avance: avancePonderado(byAspect[asp], avanceDe, "reductionTons"),
    }));
  }, [activeObjectives]);

  // ── trayectoria consolidada (v6.3) — "programado a hoy" para cada año,
  // según el calendario que cargó el usuario (NO una rampa lineal teórica) ──
  const objectiveBaseYears = activeObjectives.map((o) => getBaseYearForObjetivo(o, formByAspect));
  const trajectoryStart = objectiveBaseYears.length ? Math.min(...objectiveBaseYears) : new Date().getFullYear();
  const trajectoryEnd = Math.max(
    horizonYear || trajectoryStart + HORIZON,
    ...activeObjectives.map((o) => o.year || trajectoryStart)
  );
  const years = Array.from({ length: trajectoryEnd - trajectoryStart + 1 }, (_, i) => trajectoryStart + i);
  const trajectory = years.map((y) => {
    const corte = `${y}-12-31`;
    return avancePonderado(activeObjectives, (o) => esperadoHasta(o, corte), "reductionTons");
  });

  if (loading) return <Loader active inline="centered" />;

  return (
    <div className="strategic-plan">
      <div className="sp-head">
        <h1>{t("sp_title")}</h1>
        <p>
          {renderTemplate(t("sp_subtitle_template"), {
            strategic: <strong>{t("sp_word_strategic_objective")}</strong>,
            smart: <strong>{t("sp_word_smart_objectives")}</strong>,
            actions: <strong>{t("sp_word_goals_actions")}</strong>,
          })}
        </p>
      </div>

      {activeObjectives.length === 0 ? (
        <Message>
          <Message.Header>{t("sp_empty_title")}</Message.Header>
          <p>{t("sp_empty_body")}</p>
        </Message>
      ) : (
        <>
          <div className="sp-summary">
            <div className="sp-sum-card">
              <div className="sp-sc-label">{t("sp_summary_progress_label")}</div>
              <div className="sp-sc-value">{Math.round(global * 100)}%</div>
              <div className="sp-sc-sub">{t("sp_summary_progress_sub")}</div>
            </div>
            <div className="sp-sum-card">
              <div className="sp-sc-label">{t("sp_summary_completed_label")}</div>
              <div className="sp-sc-value">{cumplidos} / {activeObjectives.length}</div>
              <div className="sp-sc-sub">{renderTemplate(t("sp_summary_completed_sub_template"), { count: totalMetas })}</div>
            </div>
            <div className="sp-sum-card">
              <div className="sp-sc-label">{t("sp_summary_investment_label")}</div>
              <div className="sp-sc-value sp-sc-value--sm">{inversionTotal.toLocaleString("es-AR")}</div>
              <div className="sp-sc-sub">{t("sp_summary_investment_sub")}</div>
            </div>
            <div className="sp-sum-card sp-sum-card--accent">
              <div className="sp-sc-label">{t("sp_summary_status_label")}</div>
              <div className="sp-sc-value sp-sc-value--sm">
                {global >= 1 ? t("sp_status_cumplido") : global >= 0.5 ? t("sp_status_en_avance") : global > 0 ? t("sp_status_en_marcha") : t("sp_status_inicial")}
              </div>
              <div className="sp-sc-sub">{renderTemplate(t("sp_summary_status_sub_template"), { start: trajectoryStart, end: trajectoryEnd })}</div>
            </div>
          </div>

          {/* ── Nivel 1 ── */}
          <div className="sp-band"><span className="sp-band__lvl">{renderTemplate(t("sp_level_label_template"), { n: 1 })}</span> {t("sp_band_l1_title")}</div>
          <div className="sp-card sp-global">
            <div className="sp-global__txt">
              {t("sp_global_text")}
            </div>
            <div>
              <div className="sp-bar sp-bar--lg">
                <div className="sp-bar__fill" style={{ width: `${Math.min(global * 100, 100)}%`, background: progressColor(global) }} />
                <div className="sp-bar__mark" style={{ left: `${Math.min(esperadoGlobal * 100, 100)}%` }} title={t("sp_senda_title")} />
                <div className="sp-bar__lbl">{Math.round(global * 100)}%</div>
              </div>
              <div className="sp-senda-sub">
                {renderTemplate(t("sp_senda_hoy_template"), { pct: <strong>{Math.round(esperadoGlobal * 100)}%</strong> })}
                {" · "}
                <span className={sendaGlobal.ok ? "sp-senda-ok" : "sp-senda-back"}>{sendaGlobal.txt}</span>
              </div>
            </div>
            <span className={`sp-chip sp-chip--${statusChip(global, t).cls}`}>{statusChip(global, t).txt}</span>
          </div>

          {/* ── Nivel 2 ── */}
          <div className="sp-band">
            <span className="sp-band__lvl">{renderTemplate(t("sp_level_label_template"), { n: 2 })}</span> {t("sp_band_l2_title")}
            <span className="sp-band__hint">{t("sp_band_l2_hint")}</span>
          </div>
          <div className="sp-card sp-card--scroll">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>{t("sp_th_code")}</th>
                  <th style={{ textAlign: "left" }}>{t("sp_th_objective")}</th>
                  <th>{t("sp_th_start")}</th>
                  <th>{t("sp_th_goal")}</th>
                  <th>{t("sp_th_status")}</th>
                  <th>{t("sp_th_reduction_target")}</th>
                  <th>{t("responsible")}</th>
                  <th>{t("frequency")}</th>
                  <th>{t("sp_th_goals_contrib")}</th>
                  <th>{t("sp_th_progress_pct")}</th>
                  <th>{t("sp_th_state")}</th>
                  <th>{t("sp_th_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {aspectGroups.flatMap(({ aspect, objetivos, avance }) => [
                  <tr className="sp-asp-band" key={`band-${aspect}`}>
                    <td colSpan={12} style={{ borderLeft: `4px solid ${ASPECT_COLOR[aspect] || "#5E89B4"}` }}>
                      <span className="sp-dot" style={{ background: ASPECT_COLOR[aspect] || "#5E89B4", marginRight: 7 }} />
                      {t(aspect)} · {renderTemplate(t("sp_asp_band_template"), { count: objetivos.length })}
                      {" · "}
                      <span className="sp-asp-band__av">{fillTemplate(t("sp_asp_band_avance_template"), { pct: Math.round(avance * 100) })}</span>
                    </td>
                  </tr>,
                  ...objetivos.map((o) => {
                  const av = avanceDe(o);
                  const esperado = esperadoDe(o, hoyIso);
                  const senda = sendaTxt(av, esperado, t);
                  const overdue = isOverdueObjective(o);
                  const chip = overdue ? { cls: "overdue", txt: `⚠ ${t("sp_status_vencido")}` } : statusChip(av, t);
                  const color = ASPECT_COLOR[o.aspect] || "#5E89B4";
                  const baseYear = getBaseYearForObjetivo(o, formByAspect);
                  return (
                    <tr key={o._id}>
                      <td><span className="sp-code">{o.code || o.kpiCode || (o.aspect || "").slice(0, 3).toUpperCase()}</span></td>
                      <td className="sp-txt">
                        <div className="sp-obj-title"><span className="sp-dot" style={{ background: color }} />{t(o.action)} {o.source} — {o.reductionTons}%</div>
                        <div className="sp-obj-sub">{fillTemplate(t("sp_obj_sub_template"), { aspect: t(o.aspect), base: t(o.baseMedicion), baseYear })}</div>
                        {o.originalYear && (
                          <div className="sp-extended-badge">
                            {fillTemplate(t("sp_extended_badge_template"), { originalYear: o.originalYear, year: o.year })}
                          </div>
                        )}
                        {isBaseYearEstimated(o, formByAspect) && (
                          <div className="sp-estimated-badge">{t("sp_estimated_baseline_badge")}</div>
                        )}
                      </td>
                      <td>{formatMonthYearAbbrev(o.periodStart) || "—"}</td>
                      <td>{o.year}</td>
                      <td>{t(STATUS_LABEL_KEY[statusOf(o)])}</td>
                      <td><strong>{o.reductionTons}%</strong></td>
                      <td>{o.responsible || "—"}</td>
                      <td>{o.frequency ? t(o.frequency) : "—"}</td>
                      <td>
                        {renderTemplate(t("sp_meta_count_template"), { count: <strong>{(o.metas || []).length}</strong> })}
                      </td>
                      <td>
                        <div className="sp-bar">
                          <div className="sp-bar__fill" style={{ width: `${Math.min(av * 100, 100)}%`, background: progressColor(av) }} />
                          <div className="sp-bar__mark" style={{ left: `${Math.min(esperado * 100, 100)}%` }} title={t("sp_senda_title")} />
                          <div className="sp-bar__lbl">{Math.round(av * 100)}%</div>
                        </div>
                        <div className={`sp-senda-sub ${senda.ok ? "sp-senda-ok" : "sp-senda-back"}`}>{senda.txt}</div>
                      </td>
                      <td><span className={`sp-chip sp-chip--${chip.cls}`}>{chip.txt}</span></td>
                      <td>
                        <div className="sp-row-actions">
                          <button type="button" className="sp-icon-btn" title={t("update")} onClick={() => openEditObjective(o)}>
                            <Icon name="pencil" />
                          </button>
                          <button type="button" className="sp-icon-btn sp-icon-btn--danger" title={t("delete")} onClick={() => openDeleteObjective(o._id)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }),
                ])}
              </tbody>
            </table>
          </div>

          {/* Nivel 3 (metas/acciones con calendario) se editan ahora en la
              pestaña "Metas y acciones" — ver GoalsAndActions. */}

          {/* ── Cumplimiento anual de metas (v6.5) ── */}
          <div className="sp-head" style={{ marginTop: 28 }}>
            <h1 style={{ fontSize: 17 }}>{t("sp_cumplimiento_anual_title")}</h1>
            <p>{t("sp_cumplimiento_anual_body")}</p>
          </div>
          <div className="sp-card sp-card--scroll">
            <table className="sp-table sp-anual-table">
              <thead>
                <tr>
                  <th className="sp-th-meta">{t("sp_anual_col_meta")}</th>
                  {years.map((y) => <th key={y}>{y}</th>)}
                </tr>
              </thead>
              <tbody>
                {activeObjectives.flatMap((o) => [
                  <tr className="sp-asp-band" key={`anual-band-${o._id}`}>
                    <td colSpan={1 + years.length} style={{ borderLeft: `4px solid ${ASPECT_COLOR[o.aspect] || "#5E89B4"}` }}>
                      <span className="sp-code">{o.code || o.kpiCode}</span> {t(o.action)} {o.source}
                    </td>
                  </tr>,
                  ...(o.metas || []).map((m) => (
                    <tr key={m._id}>
                      <td className="sp-txt"><strong>{m.code}</strong> {m.description}</td>
                      {years.map((y) => {
                        const { cumplidas, programadas, vencidas } = cumplimientoAnualMeta(m, y, hoyIso);
                        if (!programadas) {
                          return <td key={y} className="sp-anual-cell sp-anual-cell--none">—</td>;
                        }
                        const isFuture = cumplidas === 0 && vencidas === 0;
                        const cls = cumplidas === programadas ? "ok" : vencidas > 0 ? "late" : isFuture ? "future" : "warn";
                        const label = isFuture ? t("sp_anual_cell_scheduled") : `${cumplidas}/${programadas} · ${Math.round((cumplidas / programadas) * 100)}%`;
                        const title = fillTemplate(t("sp_anual_cell_tooltip_template"), { cumplidas, programadas, year: y, vencidas });
                        return (
                          <td key={y} className={`sp-anual-cell sp-anual-cell--${cls}`} title={title}>
                            {label}
                          </td>
                        );
                      })}
                    </tr>
                  )),
                ])}
                <tr className="sp-anual-plan-row">
                  <td><strong>{t("sp_anual_row_plan")}</strong></td>
                  {years.map((y) => {
                    const { cumplidas, programadas } = cumplimientoAnual(activeObjectives, y);
                    if (!programadas) return <td key={y}>—</td>;
                    return (
                      <td key={y}>
                        <strong>{cumplidas}/{programadas} · {Math.round((cumplidas / programadas) * 100)}%</strong>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Trayectoria ── */}
          <div className="sp-head" style={{ marginTop: 28 }}>
            <h1 style={{ fontSize: 17 }}>{t("sp_trayecto_title")}</h1>
            <p>{t("sp_trayecto_body")}</p>
          </div>
          <div className="sp-card" style={{ padding: 18 }}>
            {years.map((y, i) => (
              <div className="sp-trayecto-row" key={y}>
                <div className="sp-trayecto-year">{y}</div>
                <div className="sp-trayecto-track">
                  <div className={`sp-trayecto-fill ${i === years.length - 1 ? "sp-trayecto-fill--meta" : ""}`} style={{ width: `${Math.min(trajectory[i] * 100, 100)}%` }} />
                </div>
                <div className="sp-trayecto-pct">{Math.round(trajectory[i] * 100)}%</div>
              </div>
            ))}
          </div>
        </>
      )}

      <BasicModal show={!!editingObjective} close={closeEditObjective} size="fullscreen">
        {editingObjective && (
          <EnvironmentalObjetivesForm
            objetivesForm={editingObjective}
            siteSelected={siteSelected}
            sumImpact={0}
            onClose={closeEditObjective}
            onReload={load}
          />
        )}
      </BasicModal>

      <ConfirmModal
        open={!!deleteTargetId}
        title={t("delete")}
        content={t("delete_EnvironmentalObjetives_form_with_date")}
        onConfirm={confirmDeleteObjective}
        onCancel={closeDeleteObjective}
        danger
      />
    </div>
  );
}
